import { Injectable, inject, signal, computed, effect, untracked } from '@angular/core';
import { Board } from '../models/board.model';
import { BoardApiService } from './board-api.service';
import { AuthService } from './auth.service';

/**
 * Centralized state management service for boards.
 *
 * Maintains a single source of truth for board data using signals.
 * Components should read from and write to this service instead of
 * making direct API calls, to avoid redundant requests.
 *
 * State is scoped to the user it was fetched for. This service outlives any
 * single session (providedIn: 'root'), so without that scoping one user's
 * boards stay readable in the same tab after another signs in.
 */
@Injectable({
  providedIn: 'root',
})
export class BoardStateService {
  private boardApi = inject(BoardApiService);
  private auth = inject(AuthService);

  // Private writable signals
  private boardsSignal = signal<Board[]>([]);
  private isLoadingSignal = signal(false);
  private errorSignal = signal<string | null>(null);

  // Who the boards in state belong to. Null when nothing is loaded.
  private ownerIdSignal = signal<string | null>(null);

  /** The signed-in user's id, or null. Captured before every request. */
  private currentUserId(): string | null {
    return this.auth.user()?.id ?? null;
  }

  /**
   * Boards for the currently signed-in user. The owner check happens on read,
   * so a list belonging to someone else can never be rendered or opened -
   * whatever order the auth signal and the reset effect below settle in.
   */
  readonly boards = computed(() => {
    const owner = this.ownerIdSignal();
    return owner !== null && owner === this.auth.user()?.id ? this.boardsSignal() : [];
  });

  // Public readonly signals
  readonly isLoading = this.isLoadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  // Computed: check if boards have been loaded
  readonly hasBoards = computed(() => this.boards().length > 0);

  constructor() {
    // Drop board data whenever the signed-in user changes. One place covers
    // sign-out, sign-in as someone else, and session expiry. Only fires when
    // state actually belongs to a different user, so it cannot clobber a
    // first load that is still in flight.
    effect(() => {
      const userId = this.auth.user()?.id ?? null;
      const owner = untracked(this.ownerIdSignal);
      if (owner !== null && owner !== userId) {
        this.reset();
      }
    });
  }

  /**
   * Load all boards from API and update state.
   * Only call this when you need fresh data from the backend.
   */
  loadBoards() {
    // Who this request is for, captured now. A response that lands after the
    // user changed belongs to the previous user: discard it rather than
    // stamping it with whoever is signed in by then. Bailing leaves isLoading
    // alone on purpose - the new user's own load may already be in flight.
    const requestedFor = this.currentUserId();

    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    this.boardApi.getBoards().subscribe({
      next: (boards) => {
        if (this.currentUserId() !== requestedFor) return;
        this.boardsSignal.set(boards);
        this.ownerIdSignal.set(requestedFor);
        this.isLoadingSignal.set(false);
      },
      error: (err) => {
        if (this.currentUserId() !== requestedFor) return;
        console.error('Error loading boards:', err);
        this.errorSignal.set('Failed to load boards. Please try again.');
        this.isLoadingSignal.set(false);
      },
    });
  }

  /**
   * Get a single board by ID from the in-memory state.
   * Returns undefined if board not found, or if the state belongs to a
   * different user than the one currently signed in.
   *
   * @param id Board ID
   */
  getBoard(id: string): Board | undefined {
    return this.boards().find((board) => board.id === id);
  }

  /**
   * Create a new board.
   * Optimistically adds to state, then syncs with API.
   *
   * @param boardData Partial board data
   * @param onSuccess Callback on successful creation
   * @param onError Callback on error
   */
  createBoard(
    boardData: Partial<Board>,
    onSuccess?: (board: Board) => void,
    onError?: (error: string) => void,
  ) {
    const requestedFor = this.currentUserId();

    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    this.boardApi.createBoard(boardData).subscribe({
      next: (newBoard) => {
        // The board was created under the previous user's token, so it is
        // theirs: drop it, and don't hand it to a callback either.
        if (this.currentUserId() !== requestedFor) return;
        // Add to state. Stamp the owner too: /boards/new can be opened
        // directly, without the dashboard ever having loaded the list.
        this.boardsSignal.update((boards) => [...boards, newBoard]);
        this.ownerIdSignal.set(requestedFor);
        this.isLoadingSignal.set(false);
        onSuccess?.(newBoard);
      },
      error: (err) => {
        if (this.currentUserId() !== requestedFor) return;
        console.error('Error creating board:', err);
        const errorMsg = 'Failed to create board. Please try again.';
        this.errorSignal.set(errorMsg);
        this.isLoadingSignal.set(false);
        onError?.(errorMsg);
      },
    });
  }

  /**
   * Update an existing board.
   * Optimistically updates state, then syncs with API.
   *
   * @param id Board ID
   * @param boardData Partial board data
   * @param onSuccess Callback on successful update
   * @param onError Callback on error
   */
  updateBoard(
    id: string,
    boardData: Partial<Board>,
    onSuccess?: (board: Board) => void,
    onError?: (error: string) => void,
  ) {
    const requestedFor = this.currentUserId();

    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    this.boardApi.updateBoard(id, boardData).subscribe({
      next: (updatedBoard) => {
        if (this.currentUserId() !== requestedFor) return;
        // Update in state
        this.boardsSignal.update((boards) =>
          boards.map((board) => (board.id === id ? updatedBoard : board)),
        );
        this.isLoadingSignal.set(false);
        onSuccess?.(updatedBoard);
      },
      error: (err) => {
        if (this.currentUserId() !== requestedFor) return;
        console.error('Error updating board:', err);
        const errorMsg = 'Failed to update board. Please try again.';
        this.errorSignal.set(errorMsg);
        this.isLoadingSignal.set(false);
        onError?.(errorMsg);
      },
    });
  }

  /**
   * Delete a board.
   * Optimistically removes from state immediately, then syncs with API.
   * Restores board to state if API call fails.
   *
   * @param id Board ID
   * @param onSuccess Callback on successful deletion
   * @param onError Callback on error
   */
  deleteBoard(id: string, onSuccess?: () => void, onError?: (error: string) => void) {
    // Save board reference in case we need to restore it
    const boardToDelete = this.getBoard(id);
    if (!boardToDelete) {
      onError?.('Board not found');
      return;
    }

    const requestedFor = this.currentUserId();

    // Optimistically remove from state immediately (no loading or error state changes!)
    this.boardsSignal.update((boards) => boards.filter((board) => board.id !== id));

    // Sync with API in background
    this.boardApi.deleteBoard(id).subscribe({
      next: () => {
        if (this.currentUserId() !== requestedFor) return;
        onSuccess?.();
      },
      error: (err) => {
        // Never restore the previous user's board into the new user's state.
        if (this.currentUserId() !== requestedFor) return;
        console.error('Error deleting board:', err);
        const errorMsg = 'Failed to delete board. Please try again.';

        // Restore board to state on error
        this.boardsSignal.update((boards) => [...boards, boardToDelete]);

        // DON'T touch error state - let the dashboard handle it locally with alert
        // Call error callback for local error handling (alert)
        onError?.(errorMsg);
      },
    });
  }

  /** Accept a completed editor write only for its original owner. */
  acceptSaved(board: Board, owner: string): void {
    if (this.currentUserId() !== owner) return;
    const boards = this.ownerIdSignal() === owner ? this.boardsSignal() : [];
    this.boardsSignal.set([...boards.filter((item) => item.id !== board.id), board]);
    this.ownerIdSignal.set(owner);
  }

  /**
   * Clear error state
   */
  clearError() {
    this.errorSignal.set(null);
  }

  /**
   * Force refresh boards from API
   */
  refresh() {
    this.loadBoards();
  }

  /** Discard all board state. Called when the signed-in user changes. */
  private reset() {
    this.boardsSignal.set([]);
    this.ownerIdSignal.set(null);
    this.errorSignal.set(null);
    this.isLoadingSignal.set(false);
  }
}
